import {ActivatedRoute, Params} from '@angular/router';
import {AuthenticationService} from '@app/app/shared/services/authentication.service';
import {FormControl, FormGroup} from '@angular/forms';
import {AfterViewInit, Component, EventEmitter, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {MatPaginator, MatSort, MatTableDataSource} from '@angular/material';
import {merge} from 'rxjs/observable/merge';
import {of as observableOf} from 'rxjs/observable/of';
import {catchError} from 'rxjs/operators/catchError';
import {map} from 'rxjs/operators/map';
import {startWith} from 'rxjs/operators/startWith';
import {switchMap} from 'rxjs/operators/switchMap';
import {MatDialog} from '@angular/material';
import * as moment from 'moment';
import {Subscription} from 'rxjs/Subscription';

@Component({
  selector: 'app-alarms',
  templateUrl: './alarms.component.html',
  styleUrls: ['alarms.css']
})
export class AlarmsComponent implements OnInit, AfterViewInit, OnDestroy {
  displayedColumns = ['timestamp', 'type', 'splashpad_name', 'message', 'severity'];
  dataSource = new MatTableDataSource();

  public minDate = new Date(1960, 0, 1);
  public maxDate = new Date(2100, 0, 1);
  private alarmId;

  private url = '';

  resultsLength = 0;
  isLoadingResults = true;
  name: string;
  query: string;

  filter = new Filter();

  filterForm = new FormGroup({
    query: new FormControl(),
    status: new FormControl(),
    power: new FormControl(),
    type: new FormControl(),

    start_date: new FormControl(),
    end_date: new FormControl()
  });

  colors = {
    pending: 'rgba(239, 154, 154, 0.2)',
    solved: 'rgba(165, 214, 167, 0.2)',
    planned: 'rgba(255, 224, 130, 0.2)',
    completed: 'rgba(128, 222, 234, 0.2)',
  };

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  sub1: Subscription;
  sub2: Subscription;
  sub3: Subscription;

  ngOnDestroy() {
    if (this.sub1) {
      this.sub1.unsubscribe();
    }

    if (this.sub2) {
      this.sub2.unsubscribe();
    }

    if (this.sub3) {
      this.sub3.unsubscribe();
    }
  }

  constructor(private http: HttpClient,
              private route: ActivatedRoute,
              public dialog: MatDialog,
              private auth: AuthenticationService,
              private activatedRoute: ActivatedRoute) {

  }

  private prepareUrl() {
    let sortField = '';

    if ((this.sort.direction === 'asc' || this.sort.direction === 'desc') && this.sort.active) {
      if (this.sort.direction === 'desc') {
        sortField = '-' + this.sort.active;
      } else if (this.sort.direction === 'asc') {
        sortField = this.sort.active;
      }
    }

    const page = this.paginator.pageIndex + 1 || 1;
    const query = this.filter.filterValue || '';
    const status = this.filter.statusValue || '';
    const online = this.filter.onlineValue || '';
    const type = this.filter.typeValue || '';
    const startDate = this.filter.startDateValue || '';
    const endDate = this.filter.endDateValue || '';
    const id = this.alarmId || '';

    return `${this.url}?filter[id]=${id}&filter[query]=${query}&page=${page}&sort=${sortField}&filter[online]=${online}&filter[status]=${status}&filter[type]=${type}&filter[from]=${startDate}&filter[to]=${endDate}`;
  }

  ngAfterViewInit() {
    // If the user changes the sort order, reset back to the first page.
    this.sub1 = this.sort.sortChange.subscribe(() => this.paginator.pageIndex = 0);

    this.sub2 = merge(this.filter.filterApplied, this.sort.sortChange, this.paginator.page)
      .pipe(
        startWith({}),
        switchMap(() => {
          this.isLoadingResults = true;
          return this.http.get<any>(this.prepareUrl());
        }),
        map(data => {
          this.isLoadingResults = false;
          this.resultsLength = data.meta.pagination.total;

          return data.data;
        }),
        catchError(() => {
          this.isLoadingResults = false;
          return observableOf([]);
        })
      ).subscribe(data => {
      this.dataSource.data = data;
    });

    this.dataSource.sort = this.sort;
  }

  ngOnInit() {
    switch (this.auth.userRole()) {
      case 'client':
        this.url = '/clients/' + this.auth.userId() + '/alarms';
        break;
      case 'employee':
        this.url = '/employees/' + this.auth.userId() + '/alarms';
        break;
      case 'admin':
        this.url = '/admins/' + this.auth.userId() + '/alarms';
        break;
      case 'manufacturer':
        this.url = '/manufacturers/' + this.auth.userId() + '/alarms';
        break;
      default:
        throw new Error('Incorrect user type!');
    }

    this.sub3 = this.activatedRoute.queryParams.subscribe((queryParams: Params) => {
      const newId = parseInt(queryParams['id'], 10);

      if (this.alarmId !== null && this.alarmId !== newId) {
        this.alarmId = newId;
        this.reloadData();
      } else {
        this.alarmId = newId;
      }
    });
  }

  clear() {
    this.filter.reset();

    this.minDate = new Date(1960, 0, 1);
    this.maxDate = new Date(2100, 0, 1);

    this.filterForm.patchValue({
      query: null,
      status: null,
      power: null,
      type: null,
      start_date: null,
      end_date: null,
    });
  }

  setStartDate(value) {
    const date = new Date(value);
    this.minDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    this.filter.changeStartDate(value);
  }

  setEndDate(value) {
    const date = new Date(value);
    this.maxDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());

    this.filter.changeEndDate(value);
  }

  toggleStatus(value) {
    this.filter.changeStatus(value);
  }

  toggleOnline(value) {
    this.filter.changeOnline(value);
  }

  toggleType(value) {
    this.filter.changeType(value);
  }

  applyFilter(value: string) {
    this.filter.changeFilter(value);
  }

  getColor(row) {
    if (row.attributes.type === 'alarm') {
      if (row.attributes.status === 'pending') {
        return this.colors.pending;
      } else if (row.attributes.status === 'solved') {
        return this.colors.solved;
      }
    } else if (row.attributes.type === 'task') {
      if (row.attributes.status === 'planned' || row.attributes.status === 'due') {
        return this.colors.planned;
      } else if (row.attributes.status === 'completed') {
        return this.colors.completed;
      }
    }

    return null;
  }

  printThis(type: string) {
    let popupWindow;
    const innerContents = document.getElementById(type);
    const headers = document.getElementsByClassName('mat-sort-header-button');
    const rows = document.getElementsByClassName('mat-row');

    let string = `
      <html>
        <head>
          <style>
            td {
              border-right: 1px solid darkgray;
              border-bottom: 1px solid darkgray;
            }
          </style>
        </head>
        <body onload="window.print()">

        <table>
          <tr>
            <th>${headers[0].innerHTML}</th>
            <th>${headers[1].innerHTML}</th>
            <th>${headers[2].innerHTML}</th>
            <th>${headers[3].innerHTML}</th>
            <th>${headers[4].innerHTML}</th>
          </tr>`;

    for (let i = 0; i < rows.length; i++) {
      const cells = rows[i].getElementsByClassName('mat-cell');

      string += `<tr>`;
      for (let l = 0; l < cells.length; l++) {
        string += `<td>${cells[l].innerHTML}</td>`;
      }
      string += `</tr>`;
    }

    string += `</table></body></html>`;

    popupWindow = window.open('', '_blank', 'width=1000,height=800,scrollbars=no,menubar=no,toolbar=no,location=no,status=no,titlebar=no');
    popupWindow.document.open();
    popupWindow.document.write(string);
    popupWindow.document.close();
  }

  private reloadData() {
    this.isLoadingResults = true;

    return this.http.get<any>(this.prepareUrl()).subscribe(data => {
      this.isLoadingResults = false;
      this.resultsLength = data.meta.pagination.total;
      this.dataSource.data = data.data;
    });
  }
}

export class Filter {
  filterApplied = new EventEmitter();
  filterValue = '';
  statusValue = '';
  onlineValue = '';
  typeValue = '';
  startDateValue = '';
  endDateValue = '';

  reset() {
    this.filterValue = '';
    this.statusValue = '';
    this.onlineValue = '';
    this.typeValue = '';
    this.startDateValue = '';
    this.endDateValue = '';

    this.filterApplied.emit();
  }

  changeFilter(val: string) {
    this.filterValue = val.trim();
    this.filterApplied.emit();
  }

  changeStatus(val: string) {
    this.statusValue = val.trim();
    this.filterApplied.emit();
  }

  changeOnline(val: string) {
    this.onlineValue = val.trim();
    this.filterApplied.emit();
  }

  changeType(val: string) {
    this.typeValue = val.trim();
    this.filterApplied.emit();
  }

  changeStartDate(val: Date | null) {
    if (val !== null) {
      this.startDateValue = moment(val).toISOString();
    } else {
      this.startDateValue = null;
    }

    this.filterApplied.emit();
  }

  changeEndDate(val: Date | null) {
    if (val !== null) {
      this.endDateValue = moment(val).toISOString();
    } else {
      this.endDateValue = null;
    }

    this.filterApplied.emit();
  }
}



// WEBPACK FOOTER //
// ./src/app/alarms/alarms.component.ts